import React, { useState } from 'react';
import { X, Bug, Lightbulb, Sparkles, HelpCircle, Send, Loader2 } from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { UserFeedbackType } from '../types';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const feedbackTypes: { type: UserFeedbackType; label: string; icon: React.ElementType; color: string }[] = [
  { type: 'bug', label: 'Bug Report', icon: Bug, color: 'bg-red-500' },
  { type: 'suggestion', label: 'Suggestion', icon: Lightbulb, color: 'bg-yellow-500' },
  { type: 'feature', label: 'Feature Request', icon: Sparkles, color: 'bg-purple-500' },
  { type: 'other', label: 'Other', icon: HelpCircle, color: 'bg-gray-500' },
];

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [selectedType, setSelectedType] = useState<UserFeedbackType>('suggestion');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!message.trim()) {
      setError('Please enter your feedback message');
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.submitFeedback(selectedType, subject.trim(), message.trim());
      setSuccess(true);
      setTimeout(() => {
        onClose();
        // Reset form
        setSelectedType('suggestion');
        setSubject('');
        setMessage('');
        setSuccess(false);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setError(null);
      setSuccess(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white border-2 border-[#161E54] shadow-neo rounded-none animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-[#161E54] bg-[#FFF6F4]">
          <h2 className="text-xl font-bold text-[#161E54]">Send Feedback</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-1 hover:bg-[#161E54]/10 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-[#161E54]" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {success ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center border-2 border-green-500">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#161E54] mb-2">Thank You!</h3>
              <p className="text-gray-600">Your feedback has been submitted successfully.</p>
            </div>
          ) : (
            <>
              {/* Feedback Type Selection */}
              <div>
                <label className="block text-sm font-bold text-[#161E54] mb-2">
                  What type of feedback?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {feedbackTypes.map(({ type, label, icon: Icon, color }) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setSelectedType(type)}
                      className={`flex items-center gap-2 p-3 border-2 transition-all ${
                        selectedType === type
                          ? 'border-[#161E54] bg-[#FFF6F4] shadow-neo-sm'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`p-1.5 rounded ${color}`}>
                        <Icon className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-sm font-medium text-[#161E54]">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Subject */}
              <div>
                <label htmlFor="feedback-subject" className="block text-sm font-bold text-[#161E54] mb-2">
                  Subject
                </label>
                <input
                  id="feedback-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of your feedback"
                  className="w-full px-4 py-2.5 border-2 border-[#161E54] focus:outline-none focus:shadow-neo-sm transition-shadow"
                  disabled={isSubmitting}
                />
              </div>

              {/* Message */}
              <div>
                <label htmlFor="feedback-message" className="block text-sm font-bold text-[#161E54] mb-2">
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Describe your feedback in detail..."
                  rows={4}
                  className="w-full px-4 py-2.5 border-2 border-[#161E54] focus:outline-none focus:shadow-neo-sm transition-shadow resize-none"
                  disabled={isSubmitting}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="p-3 bg-red-50 border-2 border-red-500 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#F16D34] hover:bg-[#FF986A] text-white font-bold border-2 border-[#161E54] shadow-neo hover:shadow-neo-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Submit Feedback
                  </>
                )}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
